import React, { useState, useRef } from "react";
import QuestionCard from "./QuestionCard";

const MAX_RESULTS = 50;

function normalizeWord(w) {
  return w.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function parseQuery(query) {
  // Split by | for OR groups; within each group, space = AND; - prefix = NOT
  const orGroups = query.split(/\s*\|\s*|\s+OU\s+|\s+OR\s+/i).map(group => {
    const tokens = group.trim().split(/\s+/).filter(Boolean);
    const must = [];
    const mustNot = [];
    for (const token of tokens) {
      if (token === 'E' || token === 'AND') continue;
      if (token.startsWith('-') && token.length > 1) {
        const w = normalizeWord(token.slice(1));
        if (w) mustNot.push(w);
      } else {
        const w = normalizeWord(token);
        if (w.length >= 2) must.push(w);
      }
    }
    return { must, mustNot };
  }).filter(g => g.must.length > 0 || g.mustNot.length > 0);

  return orGroups;
}

function textMatchesGroup(text, group) {
  const norm = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return (
    group.must.every(w => norm.includes(w)) &&
    group.mustNot.every(w => !norm.includes(w))
  );
}

function questionMatchesQuery(q, orGroups) {
  const searchText = [
    q.cargo || '',
    q.disciplina || '',
    q.enunciado || '',
    ...Object.values(q.alternativas || {})
  ].join(' ');
  return orGroups.some(group => textMatchesGroup(searchText, group));
}

function intersectSets(a, b) {
  const result = new Set();
  for (const x of b) {
    if (a.has(x)) result.add(x);
  }
  return result;
}

function getFilesForQuery(index, allFiles, orGroups) {
  const resultSet = new Set();

  for (const group of orGroups) {
    if (group.must.length === 0) {
      allFiles.forEach((_, i) => resultSet.add(i));
      continue;
    }

    let groupFiles = null;
    let allMatch = true;
    for (const word of group.must) {
      const fileList = index[word];
      if (!fileList) {
        allMatch = false;
        break;
      }
      if (groupFiles === null) {
        groupFiles = new Set(fileList);
      } else {
        groupFiles = intersectSets(groupFiles, fileList);
      }
    }

    if (!allMatch || groupFiles === null) {
      allFiles.forEach((_, i) => resultSet.add(i));
    } else {
      groupFiles.forEach(f => resultSet.add(f));
    }
  }

  return [...resultSet];
}

const BuscaConteudo = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [searched, setSearched] = useState(false);

  const indexRef = useRef(null);
  const abortRef = useRef(null);

  const loadIndex = async () => {
    if (indexRef.current) return indexRef.current;
    setStatus('Carregando índice de busca...');
    const res = await fetch(`${process.env.PUBLIC_URL}/search_index.json`);
    indexRef.current = await res.json();
    return indexRef.current;
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;

    // Cancel previous search
    if (abortRef.current) abortRef.current.cancelled = true;
    const abort = { cancelled: false };
    abortRef.current = abort;

    setIsLoading(true);
    setResults([]);
    setSearched(true);

    try {
      const { files: allFiles, index } = await loadIndex();
      if (abort.cancelled) return;

      const orGroups = parseQuery(q);
      if (orGroups.length === 0) {
        setStatus('Consulta inválida.');
        setIsLoading(false);
        return;
      }

      const candidateFileIndices = getFilesForQuery(index, allFiles, orGroups);
      const totalCandidateFiles = candidateFileIndices.length;

      setStatus(`Buscando em ${totalCandidateFiles} arquivo(s)...`);

      const found = [];
      let filesLoaded = 0;

      const loadFile = async (fi) => {
        const file = allFiles[fi];
        const res = await fetch(`${process.env.PUBLIC_URL}/cargos_json/${file}`);
        if (!res.ok) return;
        const data = await res.json();
        for (const qs of Object.values(data)) {
          if (!Array.isArray(qs)) continue;
          for (const q of qs) {
            if (questionMatchesQuery(q, orGroups)) {
              found.push(q);
            }
          }
        }
      };

      // Load in batches of 10 to show progressive results
      const BATCH = 10;
      for (let i = 0; i < candidateFileIndices.length; i += BATCH) {
        if (abort.cancelled) return;
        const batch = candidateFileIndices.slice(i, i + BATCH);
        await Promise.all(batch.map(loadFile));
        filesLoaded += batch.length;
        setStatus(`Carregando... ${filesLoaded}/${totalCandidateFiles} arquivos (${found.length} encontradas)`);
        setResults([...found.slice(0, MAX_RESULTS)]);
      }

      if (abort.cancelled) return;
      setStatus(
        found.length === 0
          ? 'Nenhuma questão encontrada.'
          : `${found.length} questão(ões) encontrada(s)${found.length > MAX_RESULTS ? ` — exibindo ${MAX_RESULTS}` : ''}.`
      );
      setResults(found.slice(0, MAX_RESULTS));
    } catch (err) {
      if (!abort.cancelled) setStatus('Erro ao buscar. Tente novamente.');
    } finally {
      if (!abort.cancelled) setIsLoading(false);
    }
  };

  return (
    <section className="container mx-auto mt-8">
      <form onSubmit={handleSearch} className="bg-white p-6 shadow rounded mx-auto max-w-lg">
        <div className="mb-4">
          <label className="block text-gray-700 mb-1">
            Buscar questões por conteúdo:
          </label>
          <input
            type="text"
            className="border border-gray-300 rounded p-2 w-full"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="ex: licitação contrato   |   pregão | dispensa   |   -civil"
            disabled={isLoading}
          />
          <p className="text-xs text-gray-400 mt-1">
            Espaço = E (AND) &nbsp;·&nbsp; <code>|</code> = OU (OR) &nbsp;·&nbsp; <code>-</code>palavra = NÃO (NOT)
          </p>
        </div>
        <div className="flex justify-center">
          <button
            type="submit"
            className="bg-gray-700 text-white px-6 py-3 rounded w-2/3 md:w-1/2"
            disabled={isLoading || !query.trim()}
          >
            {isLoading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </form>

      {(status || searched) && (
        <p className="text-center text-gray-600 mt-4">{status}</p>
      )}

      <div className="mt-6">
        {results.map((item, index) => (
          <QuestionCard key={index} question={item} index={index} />
        ))}
      </div>
    </section>
  );
};

export default BuscaConteudo;
